import {createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable} from '@tanstack/react-table';
import {useCallback, useMemo, useState} from 'react';
import MaintenanceRealizeForm from './realize-form';
import type MaintenanceModel from 'src/core/models/maintenanceModel';
import SearchInput from 'src/autobahn/components/search-input';
import styles from './upcoming-tab.module.scss';
import sharedStyles from 'src/autobahn/shared/styles.module.scss';
import Table from 'src/autobahn/components/table';
import IconButton from 'src/autobahn/components/button/icon-button';
import {useMaintenances, useMaintenancesRealizedByStatus} from './queries';
import {useTablePagination} from 'src/autobahn/hooks/useTablePagination';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import {formatDate} from 'src/helpers/date';
import useTimezone from 'src/hooks/use-timezone';
import {useIntl} from 'react-intl';
import {formatMeasure} from './format-measure';


type OngoingMaintenance = {
  id: number;
  deviceId: number;
  maintenanceId: number;
  status: 'inProgress';
  vehicle: string; // {plate} - {model} e.g. "ABC-1234 - Corolla"
  title: string; // maintenance title
  nextOdometer?: number;
  nextHourmeter?: number;
  nextDate?: string;
  cost: number;
}

const columnHelper = createColumnHelper<OngoingMaintenance>();

const MaintenanceOngoingTab = () => {
  const [isMaintenanceRealizeFormOpen, setMaintenanceRealizeFormOpen] = useState(false);
  const [selectedMaintenance, selectMaintenance] = useState<MaintenanceModel | null>(null);
  const [filterText, setFilterText] = useState('');

  const {timezone} = useTimezone();

  const intl = useIntl();

  const {data: maintenances} = useMaintenances();
  const {data: ongoingMaintenances} = useMaintenancesRealizedByStatus('inProgress');
  console.log('ongoingMaintenances', ongoingMaintenances);

  // const {mutate: removeMaintenance} = useRemoveInProgressMaintenance();

  const data = useMemo(() => ongoingMaintenances.reduce((accu, ongoingMaintenance) => {
    const currentMaintenancePlan = maintenances.find((maintenances) => maintenances.id === ongoingMaintenance.maintenanceId);

    if (!currentMaintenancePlan) {
      return accu;
    }

    accu.push({
      id: ongoingMaintenance.id as number,
      deviceId: ongoingMaintenance.deviceId!,
      maintenanceId: currentMaintenancePlan.id,
      status: 'inProgress',
      vehicle: ongoingMaintenance.vehicle!,
      title: currentMaintenancePlan.name,
      nextOdometer: ongoingMaintenance.nextOdometer,
      nextHourmeter: ongoingMaintenance.nextHourmeter,
      nextDate: ongoingMaintenance.nextDate ? formatDate(ongoingMaintenance.nextDate, intl.formatMessage({id: 'dateFormat'}), timezone) : undefined,
      cost: ongoingMaintenance.cost as unknown as number,
    });

    return accu;
  }, [] as OngoingMaintenance[]), [maintenances, ongoingMaintenances, timezone]);

  const openMaintenanceRealizeForm = useCallback((ongoingMaintenanceId: number) => {
    const maintenance = ongoingMaintenances.find((m) => m.id === ongoingMaintenanceId);
    selectMaintenance(maintenance!);
    setMaintenanceRealizeFormOpen(true);
  }, [ongoingMaintenances]);

  const closeMaintenanceRealizeForm = () => {
    setMaintenanceRealizeFormOpen(false);
    selectMaintenance(null);
  };

  const columns = useMemo(() => [
    columnHelper.display({
      // header: 'vehicle',
      id: 'vehicle',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            {/* 'YH2045 - Uno' */}
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedVehicle'})}</span>
            <span className={styles.rowDescription}>{row.original.vehicle}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'title',
      id: 'title',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedTitle'})}</span>
            <span className={styles.rowDescription}>{row.original.title}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'Tipo',
      id: 'kind',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedKind'})}</span>
            {!!rowValue.nextOdometer && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedOdometer'})}</span>}
            {!!rowValue.nextDate && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedDate'})}</span>}
            {!!rowValue.nextHourmeter && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedHourmeter'})}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'period',
      id: 'period',
      // enableHiding: true,
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedNextF'})}</span>
            {!!rowValue.nextOdometer && <span className={styles.rowDescription}>{formatMeasure(rowValue.nextOdometer)}</span>}
            {!!rowValue.nextDate && <span className={styles.rowDescription}>{rowValue.nextDate}</span>}
            {!!rowValue.nextHourmeter && <span className={styles.rowDescription}>{formatMeasure(rowValue.nextHourmeter)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'title',
      id: 'cost',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedCost'})}</span>
            <span className={styles.rowDescription}>{row.original.cost}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'action',
      cell: ({row}) => (
        <div className={sharedStyles.actionButtons}>
          <IconButton label={intl.formatMessage({id: 'sharedEdit'})} className={sharedStyles.actionButton} onClick={() => openMaintenanceRealizeForm(row.original.id)}>
            <i className='fas fa-edit' />
          </IconButton>
        </div>
      ),
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
  ], [openMaintenanceRealizeForm]);

  const {pagination, setPagination, currentPageSize} = useTablePagination();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilterText,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: 'includesString',
    state: {
      pagination: {
        ...pagination,
        pageSize: currentPageSize,
      },
      globalFilter: filterText,
    },
  });

  if (isMaintenanceRealizeFormOpen && selectedMaintenance) {
    return (
      <MaintenanceRealizeForm
        selectedMaintenance={selectedMaintenance}
        onCancel={closeMaintenanceRealizeForm}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <SearchInput onChange={table.setGlobalFilter} />
      </div>

      <Table table={table} hideHeaders emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})} />

      <TablePageControls
        pageCount={table.getPageCount()}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        onPageSizeChange={(newPageSize) => {
          if (newPageSize === -1) {
            table.setPageSize(data.length);
            setPagination((prev) => ({...prev, pageSize: -1}));
          } else {
            table.setPageSize(newPageSize);
            setPagination((prev) => ({...prev, pageSize: newPageSize}));
          }
        }}
        setPageIndex={table.setPageIndex}
        getCanPreviousPage={table.getCanPreviousPage}
        getCanNextPage={table.getCanNextPage}
        previousPage={table.previousPage}
        nextPage={table.nextPage}
      />
    </div>
  );
};

export default MaintenanceOngoingTab;
